import { c as _c } from "react/compiler-runtime";
// Мелкие детали карточки заявки: блок правой колонки, чип-фильтр, поле с
// правкой на месте, текстовая область в стиле Input, object URL для превью.
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { type ComponentProps, type KeyboardEvent, type ReactNode, useEffect, useState } from "react";
import type { ApplicationStatus } from "@/api";
import { cn } from "@/lib/utils";
import { errorText } from "./format";

/** Цвет стадии — тот же, что у колонок доски. */
// Холодный на входе, тёплый на проверке, зелёный на запуске; оба исхода —
// отказ и потерянный бизнес — гасятся, чтобы не спорить с рабочими стадиями.
export const STATUS_ACCENT: Record<ApplicationStatus, string> = {
  new: "bg-sky-500",
  contacted: "bg-indigo-500",
  registered: "bg-violet-500",
  filling: "bg-purple-500",
  moderation: "bg-amber-500",
  rework: "bg-orange-500",
  active: "bg-emerald-500",
  rejected: "bg-destructive",
  lost: "bg-muted-foreground"
};
export const PRIORITY_VARIANT: Record<"critical" | "high" | "normal" | "low", "default" | "secondary" | "outline" | "destructive"> = {
  critical: "destructive",
  high: "default",
  normal: "secondary",
  low: "outline"
};
export function Panel(t0) {
  const $ = _c(32);
  let action;
  let children;
  let className;
  let contentClassName;
  let count;
  let icon;
  let rest;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      icon,
      count,
      action,
      children,
      className,
      contentClassName,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = action;
    $[2] = children;
    $[3] = className;
    $[4] = contentClassName;
    $[5] = count;
    $[6] = icon;
    $[7] = rest;
    $[8] = title;
  } else {
    action = $[1];
    children = $[2];
    className = $[3];
    contentClassName = $[4];
    count = $[5];
    icon = $[6];
    rest = $[7];
    title = $[8];
  }
  let t1;
  if ($[9] !== className) {
    t1 = cn("min-w-0 rounded-xl border bg-card text-card-foreground shadow-xs", className);
    $[9] = className;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== title) {
    t2 = <span className="truncate">{title}</span>;
    $[11] = title;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== count) {
    t3 = count !== undefined && count > 0 && <span className="text-xs font-normal tabular-nums text-muted-foreground">{count}</span>;
    $[13] = count;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== icon || $[16] !== t2 || $[17] !== t3) {
    t4 = <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold">{icon}{t2}{t3}</h2>;
    $[15] = icon;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== action || $[20] !== t4) {
    t5 = <header className="flex min-h-11 items-center justify-between gap-2 px-4 pt-3 pb-2">{t4}{action}</header>;
    $[19] = action;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== contentClassName) {
    t6 = cn("px-4 pb-4", contentClassName);
    $[22] = contentClassName;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== children || $[25] !== t6) {
    t7 = <div className={t6}>{children}</div>;
    $[24] = children;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== rest || $[28] !== t1 || $[29] !== t5 || $[30] !== t7) {
    t8 = <section className={t1} {...rest}>{t5}{t7}</section>;
    $[27] = rest;
    $[28] = t1;
    $[29] = t5;
    $[30] = t7;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
}

/** Чип-переключатель — как счётчики стадий над таблицей заявок. */
export function Chip(t0) {
  const $ = _c(13);
  let active;
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      active,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = active;
    $[2] = children;
    $[3] = className;
    $[4] = props;
  } else {
    active = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
  }
  const t1 = active && "border-primary bg-primary/10";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors hover:bg-muted/50 disabled:pointer-events-none disabled:opacity-50", t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== active || $[9] !== children || $[10] !== props || $[11] !== t2) {
    t3 = <button type="button" aria-pressed={active} className={t2} {...props}>{children}</button>;
    $[8] = active;
    $[9] = children;
    $[10] = props;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export function Textarea(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30", "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <textarea className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function FieldError(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  if (!children) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <p className="text-xs text-destructive">{children}</p>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function Spinner(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("size-4 animate-spin", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Loader2 className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/**
 * Поле с правкой на месте: значение → клик → поле ввода, Enter сохраняет,
 * Esc отменяет. Пока идёт запрос, поле остаётся на месте — фокус не прыгает.
 */
export function InlineField({
  label,
  value,
  display,
  placeholder = "добавить",
  onSave,
  disabled,
  type = "text",
  inputMode,
  multiline,
  extra
}: {
  label: string;
  value: string;
  display?: ReactNode;
  placeholder?: string;
  onSave: (next: string) => Promise<unknown>;
  disabled?: boolean;
  type?: string;
  inputMode?: ComponentProps<"input">["inputMode"];
  multiline?: boolean;
  /** Кнопка рядом со значением: позвонить, написать. */
  extra?: ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setError(null);
  };
  const save = async () => {
    if (draft.trim() === value.trim()) {
      cancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
    }
    if (event.key === "Enter" && (!multiline || event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void save();
    }
  };
  return <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-x-3 py-1 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
      <span className="pt-1.5 text-xs text-muted-foreground">{label}</span>
      {editing ? <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-start gap-1">
            {multiline ? <Textarea autoFocus rows={4} value={draft} disabled={busy} aria-label={label} onChange={e_0 => setDraft(e_0.target.value)} onKeyDown={onKeyDown} /> : <Input autoFocus type={type} inputMode={inputMode} value={draft} disabled={busy} aria-label={label} className="h-8" onChange={e_1 => setDraft(e_1.target.value)} onKeyDown={onKeyDown} />}
            <Button size="icon-sm" variant="ghost" aria-label="Сохранить" disabled={busy} onClick={() => void save()}>
              {busy ? <Spinner /> : <Check className="size-4" />}
            </Button>
            <Button size="icon-sm" variant="ghost" aria-label="Отменить" disabled={busy} onClick={cancel}>
              <X className="size-4" />
            </Button>
          </div>
          <FieldError>{error}</FieldError>
        </div> : <div className="group flex min-w-0 items-start gap-1">
          <button type="button" disabled={disabled} onClick={start} className={cn("-mx-1.5 flex min-h-8 min-w-0 flex-1 items-start gap-1.5 rounded-md px-1.5 py-1.5 text-left transition-colors", !disabled && "hover:bg-muted/60")}>
            <span className={cn("min-w-0 flex-1 break-words", multiline && "whitespace-pre-line")}>
              {value ? display ?? value : <span className="text-muted-foreground">{placeholder}</span>}
            </span>
            {!disabled && <Pencil className="mt-0.5 size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />}
          </button>
          {extra}
        </div>}
    </div>;
}

/** Object URL из Blob на время жизни компонента — превью и просмотр приватных файлов. */
export function useObjectUrl(blob) {
  const $ = _c(3);
  const [url, setUrl] = useState(null);
  let t0;
  let t1;
  if ($[0] !== blob) {
    t0 = () => {
      if (!blob) {
        setUrl(null);
        return;
      }
      const next = URL.createObjectURL(blob);
      setUrl(next);
      return () => URL.revokeObjectURL(next);
    };
    t1 = [blob];
    $[0] = blob;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return url;
}
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Скачивание уже началось — ссылку можно отпускать на следующем тике.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};